import { FC, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { RouteComponentProps } from 'react-router';
import { Route, Switch } from 'react-router-dom';
import { Box } from '@chakra-ui/react';

import useCurrentPath from 'common/hooks/useCurrentPath';
import BackButton from 'components/BackButton';

import Loader from '../../../../../components/Loader';
import { useSessionStoredFormValues } from '../hooks/useSessionStoredFormValues';
import { useShouldSeeCutCardsScreen } from '../hooks/useShouldSeeCutCardsScreen';
import { AccountClosureFormValues } from '../types';
import FormProgressionBar from './components/FormProgressionBar';
import FormStepCompetition from './components/FormStepCompetition';
import FormStepInitial from './components/FormStepInitial';
import FormStepSatisfaction from './components/FormStepSatisfaction';

const Reasons: FC<RouteComponentProps> = ({ location }) => {
  const { path } = useCurrentPath();
  const { getValues, watch } = useFormContext();

  const { loading, shouldSeeCutCardsScreen } = useShouldSeeCutCardsScreen();

  const { storeFormValues } = useSessionStoredFormValues();

  const formValues = watch() as AccountClosureFormValues;

  useEffect(() => {
    storeFormValues(formValues);

    return () => {
      storeFormValues(getValues() as AccountClosureFormValues);
    };
  }, [storeFormValues, formValues, getValues]);

  if (loading) {
    return <Loader />;
  }

  return (
    <>
      <BackButton />

      <Box marginBottom="space-48">
        <Switch>
          <Route
            component={FormStepSatisfaction}
            path={`${path}/satisfaction`}
          />
          <Route component={FormStepCompetition} path={`${path}/competition`} />
          <Route component={FormStepInitial} />
        </Switch>
      </Box>

      <FormProgressionBar
        currentPath={location.pathname}
        steps={[
          {
            isNextStepAllowed: true,
            nextStepURL: `${path}/satisfaction`,
            stepPath: path,
          },
          {
            isNextStepAllowed: formValues.reasons.size > 0,
            nextStepURL: `${path}/competition`,
            stepPath: `${path}/satisfaction/:satisfaction`,
          },
          {
            isNextStepAllowed: formValues.hasLeftForCompetition !== null,
            nextStepURL: shouldSeeCutCardsScreen
              ? '/company/account-closure/cut-cards'
              : '/company/account-closure/confirm',
            stepPath: `${path}/competition`,
          },
        ]}
      />
    </>
  );
};

export default Reasons;
